// Projeto: Haklai Church — Plugin WordPress Modular - Desenvolvido por: Jefter Ruthes - https://ruthes.dev

import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import Avatar from './Avatar';
import LoadingSpinner from './LoadingSpinner';

/**
 * Componente para seleção de membros com busca
 * 
 * @param {Object} props
 * @param {number|null} props.value - ID do membro selecionado
 * @param {Function} props.onChange - Callback quando seleciona um membro (recebe memberId)
 * @param {string} props.placeholder - Placeholder do input
 * @param {string} props.label - Label do campo
 * @param {boolean} props.required - Se é obrigatório
 * @param {string|null} props.role - Slug da função para filtrar
 * @param {Array} props.excludeIds - IDs de membros a excluir da busca
 * @param {string} props.error - Mensagem de erro
 */
function MemberSelector({
  value,
  onChange,
  placeholder = 'Buscar membro...',
  label,
  required = false,
  role = null,
  excludeIds = [],
  error
}) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  // Carrega membro selecionado
  useEffect(() => {
    if (value) {
      api.get(`/members/${value}`)
        .then(response => {
          setSelectedMember(response.data.data);
          setSearch(response.data.data?.full_name || '');
        })
        .catch(() => {
          setSelectedMember(null);
          setSearch('');
        });
    } else {
      setSelectedMember(null);
      setSearch('');
    }
  }, [value]);

  // Busca membros
  const { data: membersData, isLoading } = useQuery({
    queryKey: ['members-search', search, role],
    queryFn: async () => {
      // Se não tem busca e não tem role, não busca nada para evitar carregar milhares de membros
      if (!role && (!search || search.length < 2)) {
        return { data: [] };
      }
      
      const params = new URLSearchParams({
        per_page: (!search || search.length < 2) ? '50' : '10'
      });

      if (search && search.length >= 2) {
        params.append('search', search);
      }

      if (role) {
        params.append('role', role);
      }

      const response = await api.get(`/members?${params.toString()}`);
      return response.data;
    },
    enabled: isOpen && (!!role || search.length >= 2),
  });

  const members = membersData?.data || [];

  // Filtra membros excluídos
  const filteredMembers = members.filter(member => !excludeIds.includes(member.id));

  const handleSelect = (member) => {
    setSelectedMember(member);
    setSearch(member.full_name);
    setIsOpen(false);
    onChange(member.id, member);
  };

  const handleClear = () => {
    setSelectedMember(null);
    setSearch('');
    onChange(null, null);
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleBlur = () => {
    // Delay para permitir clique nos resultados
    setTimeout(() => setIsOpen(false), 200);
  };

  return (
    <div className="form-group" style={{ position: 'relative' }}>
      {label && (
        <label className="form-label">
          {label} {required && <span style={{ color: 'var(--danger)' }}>*</span>}
        </label>
      )}

      <div style={{ position: 'relative' }}>
        {selectedMember ? (
          <div
            className="form-input"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              cursor: 'pointer',
              padding: '0.75rem 1rem'
            }}
            onClick={() => setIsOpen(!isOpen)}
          >
            <Avatar name={selectedMember.full_name} size="sm" />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '500' }}>{selectedMember.full_name}</div>
              {selectedMember.email && (
                <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                  {selectedMember.email}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                color: 'var(--text-muted)'
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '1.25rem' }}>
                close
              </span>
            </button>
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              className={`form-input ${error ? 'error' : ''}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={placeholder}
              autoComplete="off"
            />
            {isLoading && (
              <div style={{
                position: 'absolute',
                right: '1rem',
                top: '50%',
                transform: 'translateY(-50%)'
              }}>
                <LoadingSpinner size="sm" />
              </div>
            )}
          </div>
        )}

        {/* Dropdown de resultados */}
        {isOpen && (!!role || search.length >= 2) && (
          <div
            className="card"
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: '0.25rem',
              maxHeight: '300px',
              overflowY: 'auto',
              zIndex: 1000,
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}
          >
            {isLoading ? (
              <div style={{ padding: '1rem', textAlign: 'center' }}>
                <LoadingSpinner size="sm" />
              </div>
            ) : filteredMembers.length === 0 ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                Nenhum membro encontrado
              </div>
            ) : (
              filteredMembers.map(member => (
                <div
                  key={member.id}
                  onClick={() => handleSelect(member)}
                  style={{
                    padding: '0.75rem 1rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    borderBottom: '1px solid var(--border-color)',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <Avatar name={member.full_name} size="sm" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500' }}>{member.full_name}</div>
                    {member.email && (
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                        {member.email}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {error && (
        <span style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
          {error}
        </span>
      )}
    </div>
  );
}

export default MemberSelector;
